import { stringifyGraph } from '@/clients/http'
import { ReviewModel } from '@/http/models/review'
import {
  REVIEW_ADMIN_GRAPH,
  REVIEW_STATUS_APPROVED,
  REVIEW_STATUS_PENDING,
  REVIEW_STATUS_REJECTED,
  type ReviewStatus,
} from '@iws/theme-schema/reviews'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/blog'

const REVIEWS_KEY = 'reviews'

export interface ReviewListQuery {
  page: number
  length: number
  search: string
  status: ReviewStatus | 'all'
  rating: number | 'all'
}

export const useReviews = ({ page, length, search, status, rating }: ReviewListQuery) =>
  useQuery({
    queryKey: [REVIEWS_KEY, 'list', page, length, search, status, rating],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await ReviewModel.getList({
        query: {
          page,
          length,
          search: search || undefined,
          status: status === 'all' ? undefined : status,
          rating: rating === 'all' ? undefined : rating,
          order_by: 'created_at,desc',
          graph_enc: stringifyGraph(REVIEW_ADMIN_GRAPH),
        },
      })
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load reviews'))
      return res.data
    },
  })

const countFor = async (status: ReviewStatus) => {
  const res = await ReviewModel.getList({ query: { status, page: 1, length: 1, graph_enc: stringifyGraph({ _id: true }) } })
  if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not count reviews'))
  return res.data.count
}

/** Reviews per status, for the tab badges and the "awaiting approval" figure. */
export const useReviewCounts = () =>
  useQuery({
    queryKey: [REVIEWS_KEY, 'counts'],
    queryFn: async () => {
      const [pending, approved, rejected] = await Promise.all([
        countFor(REVIEW_STATUS_PENDING),
        countFor(REVIEW_STATUS_APPROVED),
        countFor(REVIEW_STATUS_REJECTED),
      ])
      return { pending, approved, rejected, all: pending + approved + rejected }
    },
  })

export const useUpdateReviewStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ReviewStatus }) => {
      const res = await ReviewModel.updateStatus({ params: { id }, body: { status } })
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not update the review'))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY] }),
  })
}

export const useDeleteReview = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await ReviewModel.delete({ params: { id } })
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not delete the review'))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REVIEWS_KEY] }),
  })
}
